import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

export type ToastTipo = 'sucesso' | 'erro' | 'aviso' | 'info';

export interface ToastItem {
  id: number;
  tipo: ToastTipo;
  texto: string;
  /** Auto-dismiss em ms. Default 4000. Passe 0 pra não fechar sozinho. */
  duracaoMs?: number;
}

interface ToastContextValue {
  sucesso: (texto: string, duracaoMs?: number) => void;
  erro: (texto: string, duracaoMs?: number) => void;
  aviso: (texto: string, duracaoMs?: number) => void;
  info: (texto: string, duracaoMs?: number) => void;
  /** API genérica caso precise. */
  push: (item: Omit<ToastItem, 'id'>) => void;
  /** Fecha por id (raramente necessário — auto-dismiss). */
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Provider global de toasts. Renderiza stack no canto inferior direito.
 * Substitui `alert()` nativo em todo o app.
 *
 * Plug uma única vez em `App.tsx` envolvendo as Routes. Consuma com `useToast()`.
 */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((item: Omit<ToastItem, 'id'>) => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev, { ...item, id }]);
  }, []);

  const sucesso = useCallback((texto: string, duracaoMs?: number) => push({ tipo: 'sucesso', texto, duracaoMs }), [push]);
  const erro    = useCallback((texto: string, duracaoMs?: number) => push({ tipo: 'erro', texto, duracaoMs }), [push]);
  const aviso   = useCallback((texto: string, duracaoMs?: number) => push({ tipo: 'aviso', texto, duracaoMs }), [push]);
  const info    = useCallback((texto: string, duracaoMs?: number) => push({ tipo: 'info', texto, duracaoMs }), [push]);

  // Identidade ESTÁVEL do value: os callbacks já são estáveis (useCallback), mas o
  // objeto literal era recriado a cada push/dismiss — todo consumer com `toast` em
  // deps de useCallback/useEffect re-disparava (ex.: um fetch com toast.erro no
  // catch entrava em LOOP infinito quando o endpoint falhava).
  const value = useMemo(
    () => ({ sucesso, erro, aviso, info, push, dismiss }),
    [sucesso, erro, aviso, info, push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

/**
 * Hook pra disparar toasts. Sintaxe:
 *   const toast = useToast();
 *   toast.sucesso('Salvo!');
 *   toast.erro('Erro ao salvar', 0);  // 0 = não fecha sozinho
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast precisa estar dentro de <ToastProvider>');
  return ctx;
}

// ── Viewport (UI) ────────────────────────────────────────────────────────────

const ToastViewport: React.FC<{ items: ToastItem[]; onDismiss: (id: number) => void }> = ({ items, onDismiss }) => {
  if (items.length === 0) return null;
  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[100] flex max-w-sm flex-col gap-2"
      role="region"
      aria-label="Notificações"
    >
      {items.map((t) => (
        <ToastCard key={t.id} item={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

const iconePorTipo: Record<ToastTipo, React.ElementType> = {
  sucesso: CheckCircleIcon,
  erro: XCircleIcon,
  aviso: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

const corPorTipo: Record<ToastTipo, { borda: string; texto: string }> = {
  sucesso: { borda: 'border-ok',      texto: 'text-ok' },
  erro:    { borda: 'border-danger',  texto: 'text-danger' },
  aviso:   { borda: 'border-warning', texto: 'text-warning' },
  info:    { borda: 'border-accent',  texto: 'text-accent-text' },
};

const ToastCard: React.FC<{ item: ToastItem; onDismiss: (id: number) => void }> = ({ item, onDismiss }) => {
  const Icon = iconePorTipo[item.tipo];
  const cores = corPorTipo[item.tipo];
  const duracao = item.duracaoMs ?? 4000;

  useEffect(() => {
    if (duracao <= 0) return;
    const t = setTimeout(() => onDismiss(item.id), duracao);
    return () => clearTimeout(t);
  }, [item.id, duracao, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={`pointer-events-auto flex items-start gap-2 rounded-iris border-l-4 bg-surface px-3 py-2 shadow-iris-xl ${cores.borda}`}
    >
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${cores.texto}`} aria-hidden />
      <p className="flex-1 text-sm text-tx">{item.texto}</p>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        className="rounded-iris-sm p-0.5 text-tx-3 hover:bg-surface-2 hover:text-tx"
        aria-label="Fechar notificação"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export default ToastProvider;
